import { STEP_KIND, type Step } from '@/shared/lib/steps-flow/types'

type ResolveResumeStepIndexParams = {
  steps: Step[]
  finishedChallengeIds: Set<number>
}

/**
 * Index of the first step the student still has something to do in.
 *
 * The flow always rebuilt its steps from the full challenge list and started at
 * index 0, so re-entering a level replayed every challenge already answered. In
 * a `block_redo` journey that is not just noise: the API rejects finishing an
 * already finished challenge of an already finished level with `challenge_done`
 * (ProgressService::ChallengeFinisher#ensure_redo_allowed!), and the student is
 * stuck on the replayed step with no way forward.
 *
 * A merged story run only counts as done when every challenge in the run is
 * done — Stories submits the whole run and aborts on the first rejection, so a
 * finished story at the head would block the unfinished ones behind it.
 *
 * Returns 0 when nothing is finished (normal start) and when everything is
 * finished (the caller decides what a fully finished level does).
 */
export function resolveResumeStepIndex({
  steps,
  finishedChallengeIds
}: ResolveResumeStepIndexParams): number {
  if (steps.length === 0 || finishedChallengeIds.size === 0) return 0

  const isStepFinished = (step: Step) => {
    if (step.kind !== STEP_KIND.CHALLENGE) return false

    const mergedIds = (step as { mergedStoryChallengeIds?: number[] })
      .mergedStoryChallengeIds

    if (mergedIds?.length) {
      return mergedIds.every(id => finishedChallengeIds.has(id))
    }

    return (
      step.challengeId !== undefined && finishedChallengeIds.has(step.challengeId)
    )
  }

  const firstUnfinished = steps.findIndex(
    step => step.kind === STEP_KIND.CHALLENGE && !isStepFinished(step)
  )

  if (firstUnfinished <= 0) return 0

  // Land on the intro of the challenge we are resuming into, when it has one:
  // dropping the student straight into a question with no context reads as a
  // glitch, and the intro carries no completion side effect.
  const resumeStep = steps[firstUnfinished]
  const previous = steps[firstUnfinished - 1]
  if (
    previous?.kind === STEP_KIND.INTRO &&
    previous.challengeId === resumeStep.challengeId
  ) {
    return firstUnfinished - 1
  }

  return firstUnfinished
}
